import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

import { Dialog } from 'antd-mobile';

import { useNavbar } from '../../../hooks/use-navbar';
import { useClient } from '../../../hooks/use-client';
import { useAuth } from '../../../hooks/use-auth';
import { apiClient } from '../../../api';
import { InterventionJson, PrestationEnum, prestationKeys } from '../../../types/models';
import { tokenStorage } from '../../../utils/token-storage';

import { BonAEnvoyer, deposerBon, viderFileBons } from './depotBon';

dayjs.locale('fr');

const PRESTATION_LABELS: Record<PrestationEnum, string> = {
  exterior: 'Nettoyage extérieur',
  interior: 'Nettoyage intérieur',
  disinfection: 'Désinfection caisse',
  decontamination: 'Décontamination',
  package: 'Nettoyage Ext & Int',
};

/* ── Signature drawing logic ── */
const useSignatureDrawing = (canvasRef: React.RefObject<HTMLCanvasElement>, onSign?: (signed: boolean) => void) => {
  const drawing = useRef(false);

  const getPos = (e: React.TouchEvent | React.MouseEvent, canvas: HTMLCanvasElement) => {
    const r = canvas.getBoundingClientRect();
    const scaleX = canvas.width / r.width;
    const scaleY = canvas.height / r.height;
    if ('touches' in e) {
      return { x: (e.touches[0].clientX - r.left) * scaleX, y: (e.touches[0].clientY - r.top) * scaleY };
    }
    return {
      x: ((e as React.MouseEvent).clientX - r.left) * scaleX,
      y: ((e as React.MouseEvent).clientY - r.top) * scaleY,
    };
  };

  const start = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    drawing.current = true;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const p = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e: React.TouchEvent | React.MouseEvent) => {
    if (!drawing.current) {
      return;
    }
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const p = getPos(e, canvas);
    ctx.lineTo(p.x, p.y);
    // Un contexte canvas n'interprète PAS les variables CSS (var(--…)) : il faut une
    // couleur explicite, sinon le trait retombe sur un défaut et peut devenir invisible.
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    if (onSign) {
      onSign(true);
    }
  };

  const stop = () => {
    drawing.current = false;
  };
  return { start, move, stop };
};

/** Remplit le canvas de blanc opaque. Sans ça son buffer est « noir transparent » :
 *  certains WebViews mobiles le compositent en noir (zone noire + signature noire
 *  invisible), et la PNG exportée aurait un fond transparent (illisible sur fond
 *  sombre et au rendu du bon). On signe donc toujours en noir sur blanc. */
const fillCanvasWhite = (canvas: HTMLCanvasElement): void => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
};

/* ── Full-screen signature pad ── */
const FullScreenSignaturePad: React.FC<{
  onValidate: (canvas: HTMLCanvasElement) => void;
  onCancel: () => void;
}> = ({ onValidate, onCancel }) => {
  const fsCanvasRef = useRef<HTMLCanvasElement>(null);
  const [hasSigned, setHasSigned] = useState(false);
  const { start, move, stop } = useSignatureDrawing(fsCanvasRef, setHasSigned);

  // Fond blanc opaque dès l'ouverture : la zone de signature doit être blanche sur
  // tous les téléphones, pas seulement via le background CSS (ignoré par certains WebViews).
  useEffect(() => {
    if (fsCanvasRef.current) {
      fillCanvasWhite(fsCanvasRef.current);
    }
  }, []);

  const clear = () => {
    const canvas = fsCanvasRef.current;
    if (!canvas) {
      return;
    }
    fillCanvasWhite(canvas);
    setHasSigned(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#f6f8fa',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            color: '#0fbdad',
            fontSize: '17px',
            cursor: 'pointer',
            padding: '8px 0',
            minWidth: '70px',
          }}
        >
          Annuler
        </button>
        <span style={{ color: '#0f172a', fontWeight: 700, fontSize: '16px', letterSpacing: '0.05em' }}>
          Signature client
        </span>
        <button
          onClick={clear}
          style={{
            background: 'none',
            border: 'none',
            color: '#0fbdad',
            fontSize: '17px',
            cursor: 'pointer',
            padding: '8px 0',
            minWidth: '70px',
            textAlign: 'right',
          }}
        >
          Effacer
        </button>
      </div>

      {/* Instruction */}
      <div style={{ textAlign: 'center', padding: '10px 16px 4px', color: '#64748b', fontSize: '13px', flexShrink: 0 }}>
        Signez dans le cadre blanc ci-dessous
      </div>

      {/* Canvas area — prend tout l'espace disponible */}
      <div style={{ flex: 1, display: 'flex', padding: '12px 16px', minHeight: 0 }}>
        <canvas
          ref={fsCanvasRef}
          width={1200}
          height={800}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={stop}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={stop}
          style={{
            width: '100%',
            height: '100%',
            background: '#ffffff',
            borderRadius: '12px',
            touchAction: 'none',
            cursor: 'crosshair',
            display: 'block',
            boxShadow: '0 0 0 2px rgba(49,220,200,0.3)',
          }}
        />
      </div>

      {/* Validate button */}
      <div style={{ padding: '12px 16px 20px', flexShrink: 0 }}>
        <button
          onClick={() => {
            if (fsCanvasRef.current) {
              onValidate(fsCanvasRef.current);
            }
          }}
          disabled={!hasSigned}
          style={{
            width: '100%',
            padding: '18px',
            background: hasSigned ? 'var(--teal)' : 'rgba(49,220,200,0.2)',
            color: hasSigned ? 'var(--navy)' : '#94a3b8',
            border: 'none',
            borderRadius: '12px',
            fontSize: '17px',
            fontWeight: 700,
            cursor: hasSigned ? 'pointer' : 'default',
          }}
        >
          Valider la signature
        </button>
      </div>
    </div>
  );
};
// Source unique des attestations : le texte affiche a l'ecran est exactement
// celui stocke dans le bon et rendu sur le PDF. Une divergence, meme
// d'apostrophe, ferait attester au client autre chose que ce qu'il a signe.
const ATTESTATIONS = {
  conformite:
    'Je confirme que le lavage/nettoyage des véhicules désignés a été exécuté conformément à la commande et sans réserve.',
  etatVehicule:
    'Je confirme qu’aucun dommage, rayure ou détérioration n’a été causé par l’intervention sur le ou les véhicules.',
  clesRendues: 'Je confirme la restitution de l’ensemble des clés, cartes d’accès ou badges confiés à l’agent.',
  clesAucune: 'Aucune clé, carte d’accès ou badge n’a été confié dans le cadre de cette intervention.',
  clientAbsent: 'Client absent lors du passage : bon établi par l’agent Camion Net.',
  nonControle: 'Je reconnais ne pas avoir vérifié les véhicules et accepte la prestation sans réserve.',
} as const;

const bonGrille: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1.3fr 0.9fr 2fr',
  gap: '10px',
  alignItems: 'center',
};

const bonEntete: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#0b1628',
};

/* ── Main component ── */
export const BonIntervention: React.FC = () => {
  const { clientId } = useParams<'clientId'>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const navbar = useNavbar();
  const { client } = useClient(clientId ? +clientId : 0);
  const { user } = useAuth();
  // Nom de l'agent : pris du compte connecté, plus besoin de le saisir.
  const agentName = user && typeof user !== 'boolean' ? user.title || user.lastname || '' : '';

  const [interventions, setInterventions] = useState<InterventionJson[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signed, setSigned] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [conformite, setConformite] = useState(false);
  const [etatVehicule, setEtatVehicule] = useState(false);
  const [keysStatus, setKeysStatus] = useState<'returned' | 'none' | null>(null);
  const [signerFunction, setSignerFunction] = useState('');
  const [clientAbsent, setClientAbsent] = useState(false);
  // Le client n'est pas d'accord : il signe « avec réserve » et précise le motif.
  const [withReserve, setWithReserve] = useState(false);
  const [reserveText, setReserveText] = useState('');
  // Le client n'a pas vérifié les véhicules et accepte la prestation sans réserve.
  const [notChecked, setNotChecked] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Parcours : l'agent valide puis passe la tablette au client, qui coche et
  // signe dans un pop-up guidé.
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);

  useEffect(() => {
    if (!navbar || !client) {
      return;
    }
    navbar.setTitle("Bon d'intervention");
    navbar.setBackArrow(true);
    navbar.setOnBack(() => {
      if (step === 2) {
        setStep(1);
      } else {
        navigate(-1);
      }
    });
    navbar.setRight();
  }, [client, step]);

  useEffect(() => {
    // Au cas ou un bon serait reste en file lors d'un passage precedent hors reseau.
    void viderFileBons();
  }, []);

  useEffect(() => {
    // Retour depuis l'édition d'une intervention (?day=…) : on rouvre directement le récap
    // du jour concerné, plutôt que de repartir de la liste des jours.
    const day = searchParams.get('day');
    if (day) {
      setSelectedDate(day);
      setStep(2);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    apiClient.interventions
      .getInterventions({ filter: `client_id||eq||${clientId}` })
      .then((res: { data: InterventionJson[] }) => setInterventions(res.data))
      .catch(console.error);

    // Les journees deja couvertes par un bon, pour les signaler dans la liste.
    const token = tokenStorage.getToken();
    fetch(`${process.env.REACT_APP_BACKEND_HOST}/api2/bons?client_id=${clientId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((bons: { day: string }[]) => setBonJours(new Set(bons.map((b) => String(b.day).slice(0, 10)))))
      .catch(() => undefined);
  }, [clientId]);

  const days = Array.from(new Set(interventions.map((i) => dayjs(i.timestamp).format('YYYY-MM-DD')))).sort((a, b) =>
    a < b ? 1 : -1,
  );

  const dayInterventions = interventions.filter((i) => dayjs(i.timestamp).format('YYYY-MM-DD') === selectedDate);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
  };

  /**
   * Depose le bon dans le compte du client, en plus de l'email.
   *
   * La signature est prise sur le canvas tel qu'il a ete montre, et le contenu
   * fige : le serveur ne recompose rien a partir des interventions, qui peuvent
   * changer apres coup. Une panne reseau ne bloque pas — le bon part en file
   * d'attente et repart plus tard.
   */
  const deposerLeBon = async (): Promise<void> => {
    if (!client || !selectedDate) {
      return;
    }
    // Client absent : pas de pad de signature à l'écran, donc pas de canvas. La
    // signature n'est requise que lorsque le client signe.
    const canvas = canvasRef.current;
    if (!clientAbsent && !canvas) {
      return;
    }

    const prestations = dayInterventions.map((i: InterventionJson) => ({
      vehicule: [i.immatriculation, i.parc_number].filter(Boolean).join(' / '),
      type: i.prestation_costs.title,
      details: prestationKeys
        .filter((k) => i[k])
        .map((k) => PRESTATION_LABELS[k])
        .join(', '),
    }));
    const clesAttestation = keysStatus === 'returned' ? ATTESTATIONS.clesRendues : ATTESTATIONS.clesAucune;
    const attestations = clientAbsent
      ? [ATTESTATIONS.clientAbsent]
      : withReserve
      ? [`RÉSERVE ÉMISE PAR LE CLIENT : ${reserveText.trim()}`, clesAttestation]
      : notChecked
      ? [ATTESTATIONS.nonControle, clesAttestation]
      : [ATTESTATIONS.conformite, ATTESTATIONS.etatVehicule, clesAttestation];

    const bon: BonAEnvoyer = {
      ref: `${client.id}-${selectedDate}-${Date.now()}`,
      clientId: client.id,
      day: selectedDate,
      signedAt: new Date().toISOString(),
      signerName: clientAbsent ? '' : signerName,
      signerFunction: clientAbsent ? '' : signerFunction,
      agentName,
      clientAbsent,
      gps,
      content: { prestations, attestations, ...(withReserve && !clientAbsent ? { reserve: reserveText.trim() } : {}) },
      signatureBase64: clientAbsent || !canvas ? undefined : canvas.toDataURL('image/png').split(',')[1],
    };
    await deposerBon(bon);
  };

  const [gps, setGps] = useState<{ lat: number; lng: number; accuracy?: number } | undefined>(undefined);
  const [bonJours, setBonJours] = useState<Set<string>>(new Set());
  const [envoi, setEnvoi] = useState<'idle' | 'sending' | 'done'>('idle');

  // La position est relevee des l'entree dans le recap : au moment de valider,
  // elle est deja prete. Un refus ou un echec ne bloque jamais — on part sans.
  useEffect(() => {
    if (step !== 2 || gps || !navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => undefined,
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, [step, gps]);

  const handleValider = async () => {
    if (!client) {
      return;
    }
    setEnvoi('sending');
    await deposerLeBon();
    setEnvoi('done');
    setShowWizard(false);
    // Confirme au client que le bon est enregistre ; a la fermeture, retour a
    // la fiche du client.
    await Dialog.alert({
      title: "Bon d'intervention enregistré",
      content: 'Le bon a bien été généré et rattaché au compte du client.',
      confirmText: 'Terminer',
    });
    navigate(`/client/${client.id}`);
  };

  /* ── STEP 1 ── */
  if (step === 1) {
    // Lancement 1er octobre : seul le bon du JOUR est signable. Les jours passés non signés
    // sont figés en « Bon d'intervention non produit » (impossible de signer a posteriori).
    const today = dayjs().format('YYYY-MM-DD');
    // Staging uniquement : on autorise la signature des jours passés pour permettre les tests.
    // En prod (hostname sans « staging »), seul le jour même reste signable.
    const isTestEnv = typeof window !== 'undefined' && window.location.hostname.includes('staging');
    return (
      <div style={{ background: '#f6f8fa', minHeight: '100%' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
          <p style={{ margin: 0, color: '#64748b', fontSize: '13px', fontStyle: 'italic' }}>
            Sélectionnez la date des prestations à faire valider.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {days.length === 0 && (
            <p style={{ color: '#64748b', textAlign: 'center', marginTop: '40px' }}>Aucune intervention enregistrée.</p>
          )}
          {days.map((day) => {
            const count = interventions.filter((i) => dayjs(i.timestamp).format('YYYY-MM-DD') === day).length;
            const isToday = day === today;
            const hasBon = bonJours.has(day);
            // Un bon déjà généré/signé n'est jamais refaisable. Sinon : prod = jour même, staging = tous les jours (tests).
            const signable = (isToday || isTestEnv) && !hasBon;
            const statut = hasBon
              ? { label: 'Bon généré', color: '#0f7a5a', background: 'rgba(16,185,129,0.14)' }
              : signable
              ? { label: 'Bon à faire', color: '#8a4b00', background: 'rgba(245,158,11,0.14)' }
              : { label: "Bon d'intervention non produit", color: '#64748b', background: 'rgba(100,116,139,0.12)' };
            return (
              <div
                key={day}
                onClick={
                  signable
                    ? () => {
                        setSelectedDate(day);
                        setStep(2);
                      }
                    : undefined
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  borderBottom: '1px solid #e2e8f0',
                  background: '#fff',
                  cursor: signable ? 'pointer' : 'default',
                  opacity: signable || hasBon ? 1 : 0.72,
                }}
              >
                <div>
                  <div style={{ color: '#0f172a', fontWeight: 600, fontSize: '15px' }}>
                    {dayjs(day).format('dddd D MMMM YYYY')}
                  </div>
                  <div style={{ color: '#0fbdad', fontSize: '12px', marginTop: '2px' }}>
                    {count} véhicule{count > 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '999px',
                      textAlign: 'right',
                      color: statut.color,
                      background: statut.background,
                    }}
                  >
                    {statut.label}
                  </span>
                  {signable && <span style={{ color: '#94a3b8', fontSize: '18px' }}>›</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ height: '80px' }} />
      </div>
    );
  }

  /* ── STEP 2 ── */
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: '#f6f8fa',
    zIndex: 9000,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
  };
  const champStyle: React.CSSProperties = {
    width: '100%',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '10px 12px',
    color: '#0f172a',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  };
  const sousTitreReserve: React.CSSProperties = {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: '0 0 8px',
  };
  const btnSecondaire: React.CSSProperties = {
    padding: '14px 18px',
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '10px',
    color: '#64748b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const btnPrimaire = (on: boolean): React.CSSProperties => ({
    padding: '16px',
    background: on ? 'var(--teal)' : 'rgba(49,220,200,0.15)',
    color: on ? 'var(--navy)' : '#94a3b8',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: on ? 'pointer' : 'default',
    letterSpacing: '0.04em',
  });
  const renderVehiculesTable = (onRowClick?: (i: InterventionJson) => void) => {
    // En mode éditable, une colonne « Modifier » explicite en fin de ligne.
    const grid: React.CSSProperties = onRowClick
      ? { ...bonGrille, gridTemplateColumns: '1fr 1.1fr 0.7fr 1.6fr auto' }
      : bonGrille;
    return (
      <div>
        <div
          style={{
            ...grid,
            padding: '10px 16px',
            background: '#f1f5f9',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <span style={bonEntete}>Type véhicule</span>
          <span style={bonEntete}>Immatriculation</span>
          <span style={bonEntete}>Parc</span>
          <span style={bonEntete}>Prestations</span>
          {onRowClick && <span style={bonEntete} />}
        </div>
        {dayInterventions.map((i) => (
          <div
            key={i.id}
            onClick={onRowClick ? () => onRowClick(i) : undefined}
            style={{
              ...grid,
              padding: '12px 16px',
              borderBottom: '1px solid #f1f5f9',
              background: '#fff',
              cursor: onRowClick ? 'pointer' : 'default',
            }}
          >
            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>{i.prestation_costs.title}</span>
            <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13px' }}>{i.immatriculation || '–'}</span>
            <span style={{ color: '#64748b', fontSize: '13px' }}>{i.parc_number || '–'}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {prestationKeys
                .filter((k) => i[k])
                .map((k) => (
                  <span
                    key={k}
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#2563eb',
                      background: 'rgba(59,130,246,0.12)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderRadius: '10px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {PRESTATION_LABELS[k]}
                  </span>
                ))}
            </div>
            {onRowClick && (
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#0fbdad',
                  background: 'rgba(15,189,173,0.12)',
                  border: '1px solid rgba(15,189,173,0.4)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  whiteSpace: 'nowrap',
                }}
              >
                ✎ Modifier
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const attestationsBase = withReserve ? reserveText.trim().length > 0 : notChecked ? true : conformite && etatVehicule;
  const attestationsOk = attestationsBase && keysStatus !== null;
  const wizardCanNext =
    wizardStep === 1
      ? attestationsOk && signerName.trim().length > 0 && signerFunction.trim().length > 0
      : wizardStep === 2
      ? signed
      : true;

  const etapesClient = ['Vos véhicules', 'Attestations', 'Signature'];

  return (
    <div style={{ background: '#f6f8fa', minHeight: '100%' }}>
      {/* En-tête */}
      <div style={{ background: '#fff', padding: '20px 16px', borderBottom: '2px solid #e2e8f0', textAlign: 'center' }}>
        <div
          style={{
            fontSize: '11px',
            letterSpacing: '0.2em',
            color: 'var(--teal)',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          Bon d'intervention
        </div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#0f172a', marginTop: '4px' }}>Camion Net</div>
      </div>

      {/* Info */}
      <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f6f8fa' }}>
        <Row label="Client" value={client?.name ?? ''} />
        <Row label="Date" value={dayjs(selectedDate).format('dddd D MMMM YYYY')} />
      </div>

      {/* Prestations */}
      <div style={{ padding: '16px 0', background: '#f6f8fa' }}>
        <SectionTitle>Prestations réalisées</SectionTitle>
        <div
          style={{
            margin: '0 16px 12px',
            padding: '12px 14px',
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: '10px',
            color: '#8a4b00',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          ⚠️ Vérifiez chaque intervention (type, immatriculation, n° de parc, prestations) avant de faire signer le
          client. Touchez « Modifier » pour corriger une ligne.
        </div>
        {renderVehiculesTable((i) => navigate(`/client/${clientId}/intervention/${i.id}?from=bon&day=${selectedDate}`))}
      </div>

      {/* Partie agent : vérifie, se nomme, puis passe la tablette au client. */}
      <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', background: '#f6f8fa' }}>
        <SectionTitle>Validation agent</SectionTitle>
        <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 12px', padding: '0 2px' }}>
          Une fois les interventions vérifiées, faites valider par le client.
        </p>

        <div
          onClick={() => {
            if (clientAbsent) {
              setClientAbsent(false);
              return;
            }
            void Dialog.confirm({
              content: (
                <div style={{ color: '#0f172a', fontSize: '14px', lineHeight: 1.55 }}>
                  <div style={{ fontWeight: 800, fontSize: '16px', marginBottom: '10px' }}>Confirmer l’absence du client</div>
                  Le bon sera établi SANS signature et indiquera que le client était absent lors du passage.{' '}
                  <span style={{ color: '#dc2626', fontWeight: 700 }}>Cette mention est visible par le client dans son espace.</span>{' '}
                  Ne cochez cette case que si le client est réellement absent :{' '}
                  <span style={{ color: '#dc2626', fontWeight: 700 }}>
                    une déclaration inexacte engage la responsabilité de Camion Net et peut se retourner contre vous.
                  </span>
                </div>
              ),
              confirmText: 'Oui, le client est absent',
              cancelText: 'Annuler',
              onConfirm: () => setClientAbsent(true),
            });
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            background: clientAbsent ? 'rgba(245,158,11,0.1)' : '#fff',
            border: `1px solid ${clientAbsent ? 'rgba(245,158,11,0.5)' : '#e2e8f0'}`,
            borderRadius: '10px',
            padding: '14px',
            marginBottom: '14px',
          }}
        >
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '6px',
              flexShrink: 0,
              border: `2px solid ${clientAbsent ? '#f59e0b' : '#cbd5e1'}`,
              background: clientAbsent ? '#f59e0b' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {clientAbsent && <span style={{ color: '#fff', fontSize: '14px', fontWeight: 900, lineHeight: 1 }}>✓</span>}
          </div>
          <span style={{ color: '#0f172a', fontSize: '17px', fontWeight: 600 }}>
            Client absent — bon établi sans signature.
          </span>
        </div>

        {clientAbsent ? (
          <button
            onClick={handleValider}
            disabled={envoi === 'sending'}
            style={{
              ...btnPrimaire(envoi !== 'sending'),
              width: '100%',
            }}
          >
            {envoi === 'sending'
              ? 'Enregistrement…'
              : envoi === 'done'
              ? '✓ Bon enregistré'
              : 'Établir le bon (client absent)'}
          </button>
        ) : (
          <button
            onClick={() => {
              setWizardStep(0);
              setShowWizard(true);
            }}
            style={{ ...btnPrimaire(true), width: '100%' }}
          >
            Faire valider par le client →
          </button>
        )}
      </div>

      {/* Canvas caché : lu au dépôt pour générer la signature. */}
      <canvas ref={canvasRef} width={640} height={200} style={{ display: 'none' }} />
      <div style={{ height: '60px', background: '#f6f8fa' }} />

      {/* ── Pop-up : parcours client ── */}
      {showWizard && (
        <div style={{ ...overlayStyle, background: '#fdf2f2' }}>
          {/* En-tête PARTIE CLIENT (thème rouge, visible à chaque étape) */}
          <div
            style={{
              background: '#dc2626',
              color: '#fff',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  opacity: 0.9,
                }}
              >
                Partie client
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, marginTop: '2px' }}>
                Validation client · {wizardStep + 1}/{etapesClient.length}
              </div>
            </div>
            <button
              onClick={() => setShowWizard(false)}
              style={{
                background: 'rgba(255,255,255,0.18)',
                border: 'none',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
              }}
            >
              Fermer
            </button>
          </div>

          {/* Progression */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              justifyContent: 'center',
              padding: '12px 0',
              background: '#fdf2f2',
              borderBottom: '1px solid #f6d5d5',
              flexShrink: 0,
            }}
          >
            {etapesClient.map((_, k) => (
              <span
                key={k}
                style={{
                  width: k === wizardStep ? '22px' : '8px',
                  height: '8px',
                  borderRadius: '999px',
                  background: k <= wizardStep ? '#dc2626' : '#f0c9c9',
                  transition: 'all .2s',
                }}
              />
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', background: '#fdf2f2' }}>
            <div
              style={{
                padding: '0 16px 12px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#dc2626',
              }}
            >
              {etapesClient[wizardStep]}
            </div>

            {wizardStep === 0 && (
              <>
                <p style={{ color: '#334155', fontSize: '14px', margin: '0 16px 14px' }}>
                  Voici les véhicules traités ce jour. Vérifiez qu'ils sont bien les vôtres et conformes aux prestations
                  demandées.
                </p>
                {renderVehiculesTable()}
              </>
            )}

            {wizardStep === 1 && (
              <div style={{ padding: '0 16px', display: 'grid', gap: '18px' }}>
                {withReserve ? (
                  <div>
                    <p style={sousTitreReserve}>Réserve du client</p>
                    <textarea
                      value={reserveText}
                      onChange={(e) => setReserveText(e.target.value)}
                      placeholder="Décrivez précisément la réserve (ex. rayure portière avant droite, lavage intérieur non fait…)"
                      rows={4}
                      style={{ ...champStyle, resize: 'vertical', minHeight: '96px', fontFamily: 'inherit' }}
                    />
                    <p style={{ color: '#94a3b8', fontSize: '11px', margin: '8px 2px 0', lineHeight: 1.5 }}>
                      Le client signera « avec réserve » : le motif est enregistré sur le bon.
                    </p>
                  </div>
                ) : notChecked ? null : (
                  <>
                    <div>
                      <p style={sousTitreReserve}>1. Conformité de la prestation</p>
                      <CheckItem checked={conformite} onChange={setConformite} label={ATTESTATIONS.conformite} />
                    </div>
                    <div>
                      <p style={sousTitreReserve}>2. État du véhicule</p>
                      <CheckItem checked={etatVehicule} onChange={setEtatVehicule} label={ATTESTATIONS.etatVehicule} />
                    </div>
                  </>
                )}
                <div>
                  <p style={sousTitreReserve}>3. Restitution des clés &amp; accès</p>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <CheckItem
                      checked={keysStatus === 'returned'}
                      onChange={(v) => setKeysStatus(v ? 'returned' : null)}
                      label={ATTESTATIONS.clesRendues}
                    />
                    <CheckItem
                      checked={keysStatus === 'none'}
                      onChange={(v) => setKeysStatus(v ? 'none' : null)}
                      label={ATTESTATIONS.clesAucune}
                    />
                  </div>
                </div>
                {!notChecked && (
                  <button
                    type="button"
                    onClick={() => {
                      setWithReserve((v) => !v);
                      setConformite(false);
                      setEtatVehicule(false);
                      setReserveText('');
                    }}
                    style={{
                      background: '#fff',
                      border: '1.5px solid #dc2626',
                      color: '#dc2626',
                      fontSize: '14px',
                      fontWeight: 700,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      padding: '14px',
                      width: '100%',
                    }}
                  >
                    {withReserve ? '← Revenir aux attestations' : 'Émettre une réserve'}
                  </button>
                )}
                {!withReserve && (
                  <div>
                    <p style={{ ...sousTitreReserve, color: '#b45309' }}>Contrôle non effectué</p>
                    <CheckItem
                      checked={notChecked}
                      onChange={(v) => {
                        setNotChecked(v);
                        if (v) {
                          setConformite(false);
                          setEtatVehicule(false);
                        }
                      }}
                      label={ATTESTATIONS.nonControle}
                      variant="amber"
                    />
                  </div>
                )}
                <div>
                  <p style={sousTitreReserve}>Vos coordonnées</p>
                  <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Nom et prénom
                  </label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Nom Prénom"
                    style={{ ...champStyle, marginBottom: '12px' }}
                  />
                  <label style={{ color: '#64748b', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Poste / fonction
                  </label>
                  <input
                    type="text"
                    value={signerFunction}
                    onChange={(e) => setSignerFunction(e.target.value)}
                    placeholder="ex. Chauffeur, Chef de parc"
                    style={champStyle}
                  />
                </div>
              </div>
            )}

            {wizardStep === 2 && (
              <div style={{ padding: '0 16px' }}>
                {withReserve && (
                  <div
                    style={{
                      background: 'rgba(220,38,38,0.08)',
                      border: '1px solid rgba(220,38,38,0.3)',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: '14px',
                      color: '#991b1b',
                      fontSize: '12px',
                      lineHeight: 1.5,
                    }}
                  >
                    <b>Signature avec réserve.</b> Motif : {reserveText.trim() || '—'}
                  </div>
                )}
                {notChecked && (
                  <div
                    style={{
                      background: 'rgba(220,38,38,0.08)',
                      border: '1px solid rgba(220,38,38,0.3)',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: '14px',
                      color: '#991b1b',
                      fontSize: '12px',
                      lineHeight: 1.5,
                    }}
                  >
                    <b>Le client déclare ne pas avoir vérifié les véhicules</b> et accepte la prestation sans réserve.
                  </div>
                )}
                {signed ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        flex: 1,
                        background: '#fff',
                        borderRadius: '8px',
                        padding: '4px',
                        boxShadow: '0 0 0 2px rgba(220,38,38,0.3)',
                      }}
                    >
                      <img
                        src={canvasRef.current?.toDataURL()}
                        alt="signature"
                        style={{ width: '100%', height: '80px', objectFit: 'contain', display: 'block' }}
                      />
                    </div>
                    <button onClick={clearSignature} style={btnSecondaire}>
                      Refaire
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSignaturePad(true)}
                    style={{
                      width: '100%',
                      padding: '22px',
                      background: '#fff',
                      border: '2px dashed rgba(220,38,38,0.4)',
                      borderRadius: '10px',
                      color: '#dc2626',
                      fontSize: '15px',
                      cursor: 'pointer',
                    }}
                  >
                    ✍️ Appuyer pour signer
                  </button>
                )}
                <div style={{ marginTop: '14px', fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 }}>
                  Signé le {dayjs().format('DD/MM/YYYY à HH:mm')}
                  {gps ? ` · position relevée (±${Math.round(gps.accuracy ?? 0)} m)` : ' · position non disponible'}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '14px 16px',
              borderTop: '1px solid #f6d5d5',
              background: '#fff',
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => (wizardStep === 0 ? setShowWizard(false) : setWizardStep(wizardStep - 1))}
              style={btnSecondaire}
            >
              {wizardStep === 0 ? 'Annuler' : '‹ Précédent'}
            </button>
            {wizardStep < etapesClient.length - 1 ? (
              <button
                onClick={() => {
                  if (!wizardCanNext) {
                    return;
                  }
                  const next = wizardStep + 1;
                  setWizardStep(next);
                  // Dernière étape (signature) : on ouvre directement le pad, sans clic en plus.
                  if (next === etapesClient.length - 1 && !signed) {
                    setShowSignaturePad(true);
                  }
                }}
                disabled={!wizardCanNext}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: wizardCanNext ? '#dc2626' : 'rgba(220,38,38,0.15)',
                  color: wizardCanNext ? '#fff' : '#d9a3a3',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: wizardCanNext ? 'pointer' : 'default',
                  letterSpacing: '0.04em',
                }}
              >
                Suivant ›
              </button>
            ) : (
              <button
                onClick={handleValider}
                disabled={!(signed && envoi !== 'sending')}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: signed && envoi !== 'sending' ? '#dc2626' : 'rgba(220,38,38,0.15)',
                  color: signed && envoi !== 'sending' ? '#fff' : '#d9a3a3',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: signed && envoi !== 'sending' ? 'pointer' : 'default',
                  letterSpacing: '0.04em',
                }}
              >
                {envoi === 'sending' ? 'Enregistrement…' : 'Valider et signer'}
              </button>
            )}
          </div>
        </div>
      )}

      {showSignaturePad && (
        <FullScreenSignaturePad
          onValidate={(fsCanvas: HTMLCanvasElement) => {
            const dst = canvasRef.current;
            if (!dst) {
              return;
            }
            const ctx = dst.getContext('2d');
            if (!ctx) {
              return;
            }
            // Fond blanc opaque avant la copie : la PNG exportée (toDataURL) et l'aperçu
            // sont ainsi noir-sur-blanc, lisibles partout (écran, bon, fond sombre).
            fillCanvasWhite(dst);
            ctx.drawImage(fsCanvas, 0, 0, dst.width, dst.height);
            setSigned(true);
            setShowSignaturePad(false);
          }}
          onCancel={() => setShowSignaturePad(false)}
        />
      )}
    </div>
  );
};

const CheckItem: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; variant?: 'red' | 'amber' }> = ({
  checked,
  onChange,
  label,
  variant = 'red',
}) => {
  const c =
    variant === 'amber'
      ? { solid: '#d97706', soft: 'rgba(217,119,6,0.10)', border: 'rgba(217,119,6,0.5)' }
      : { solid: '#dc2626', soft: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.5)' };
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        cursor: 'pointer',
        background: checked ? c.soft : '#fff',
        border: `1px solid ${checked ? c.border : '#e2e8f0'}`,
        borderRadius: '10px',
        padding: '14px',
      }}
    >
      <div
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '6px',
          flexShrink: 0,
          marginTop: '1px',
          border: `2px solid ${checked ? c.solid : '#cbd5e1'}`,
          background: checked ? c.solid : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked && <span style={{ color: '#fff', fontSize: '14px', fontWeight: 900, lineHeight: 1 }}>✓</span>}
      </div>
      <span style={{ color: checked ? '#0f172a' : '#64748b', fontSize: '17px', lineHeight: '1.5' }}>{label}</span>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
    <span style={{ color: '#0fbdad', fontSize: '13px' }}>{label}</span>
    <span style={{ color: '#0f172a', fontSize: '13px', fontWeight: 600 }}>{value}</span>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      padding: '0 16px 10px',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'var(--teal)',
    }}
  >
    {children}
  </div>
);
